import { ReplicaDatabase } from '../db/replica-database';
import { SyncEngine } from '../engine/sync-engine';
import { FileStore } from '../files/file-store';
import { HttpClient } from '../http/http-client';
import { uuidv7 } from '../ids/uuidv7';
import { applyOutbox } from '../merge/apply-outbox';
import { materialize } from '../merge/materialize';
import type { MutationKind } from '../types/mutation-kind';
import type { RecordView } from '../types/record-view';

export interface ReplicaOptions {
  /** IndexedDB database name. */
  readonly name: string;
  /** Where the sync API lives, e.g. "/api". */
  readonly baseUrl: string;
  readonly getToken: () => string | null;
  readonly maxFileBytes?: number;
}

/**
 * A full local replica of the server's records. Reads are the server's state with the outbox
 * applied on top; writes append a mutation to the outbox and ask the engine to sync soon.
 * Validation is the caller's job: the replica stores whatever patch it is given.
 */
export class Replica {
  readonly db: ReplicaDatabase;
  readonly http: HttpClient;
  readonly files: FileStore;
  readonly engine: SyncEngine;
  private actor = '';

  constructor(options: ReplicaOptions) {
    this.db = new ReplicaDatabase(options.name);
    this.http = new HttpClient({ baseUrl: options.baseUrl, getToken: options.getToken });
    this.files = new FileStore(this.db, this.http, options.maxFileBytes ?? 50 * 1024 * 1024);
    this.engine = new SyncEngine(this.db, this.http, this.files, () => options.getToken() !== null);
  }

  /** Who local edits are attributed to until the server confirms them. */
  setActor(userId: string): void {
    this.actor = userId;
  }

  async list(collection: string): Promise<RecordView[]> {
    const [records, outbox] = await Promise.all([
      this.db.records.where('collection').equals(collection).toArray(),
      this.db.outbox.where('collection').equals(collection).sortBy('seq'),
    ]);
    return materialize(records, outbox, this.actor);
  }

  async listAll(): Promise<RecordView[]> {
    const [records, outbox] = await Promise.all([
      this.db.records.toArray(),
      this.db.outbox.orderBy('seq').toArray(),
    ]);
    return materialize(records, outbox, this.actor);
  }

  async get(id: string): Promise<RecordView | undefined> {
    const [record, outbox] = await Promise.all([
      this.db.records.get(id),
      this.db.outbox.where('record_id').equals(id).sortBy('seq'),
    ]);
    return applyOutbox(record, outbox, this.actor);
  }

  /** Queue a mutation; returns the record id (a new UUIDv7 when `recordId` is omitted). */
  async mutate(
    collection: string,
    kind: MutationKind,
    patch: Record<string, unknown>,
    recordId: string = uuidv7(),
  ): Promise<string> {
    await this.db.outbox.add({
      op_id: uuidv7(),
      collection,
      record_id: recordId,
      kind,
      patch,
      client_ts: new Date().toISOString(),
      attempts: 0,
    });
    this.engine.soon();
    return recordId;
  }

  /** Forget everything on this device (on sign-out). */
  async wipe(): Promise<void> {
    const { db } = this;
    await db.transaction('rw', [db.records, db.outbox, db.blobs, db.rejections, db.meta], async () => {
      await Promise.all([
        db.records.clear(),
        db.outbox.clear(),
        db.blobs.clear(),
        db.rejections.clear(),
        db.meta.clear(),
      ]);
    });
    this.engine.reset();
  }
}
